import { Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, FormRecord, ReactiveFormsModule, Validators } from '@angular/forms';
import { DropdownData, GenericDropdownComponent, CurrencyInputFieldComponent } from '@zayan-erp/shared';
import { TextareaModule } from '@openng/optimus-ui/textarea';
import { InputNumber } from '@openng/optimus-ui/inputnumber';
import {
  DsmProductAttributeGroup,
  DsmProductOption,
  DsmSelectedProductForm,
  DsmSelectedProductValues,
  ProjectModelRoomQuantityLookup
} from '../../../models/dsm-deal-options.models';
import { DsmModelElevationProductsService } from '../../../services/dsm-model-elevation-products.service';
import { rxResourceFixed } from '@zayan-erp/shared';
import { map, NEVER, of, startWith } from 'rxjs';
import { Message } from '@openng/optimus-ui/message';
import { LocationItem } from '@zayan-erp/home-building';

@Component({
  selector: 'dsm-selected-product-config-form',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    GenericDropdownComponent,
    TextareaModule,
    CurrencyInputFieldComponent,
    InputNumber,
    Message
  ],
  templateUrl: './dsm-selected-product-config-form.component.html'
})
export class DsmSelectedProductConfigFormComponent {
  readonly #modelElevationProductsService = inject(DsmModelElevationProductsService);
  readonly #fb = inject(FormBuilder);

  readonly product = input.required<DsmProductOption>();
  readonly initialValues = input<DsmSelectedProductValues | null>(null);
  readonly addendumId = input<string | null>(null);
  // Location source before an addendum exists (appointment screens); ignored when addendumId is set.
  readonly lotId = input<string | null>(null);
  readonly preloadedLocations = input<LocationItem[] | null>();
  readonly commit = output<DsmSelectedProductValues>();

  readonly form: DsmSelectedProductForm = this.#fb.group({
    locationId: this.#fb.control<string | null>(null),
    quantity: this.#fb.control<number | null>(null, [Validators.required]),
    unitPrice: this.#fb.control<number | null>(null, [Validators.required]),
    notes: this.#fb.nonNullable.control(''),
    attributeValues: this.#fb.record<FormControl<string | null>>({})
  });

  readonly submitAttempted = signal(false);

  readonly #formValues = toSignal(this.form.valueChanges.pipe(startWith(this.form.getRawValue())), {
    initialValue: this.form.getRawValue()
  });

  readonly totalQty = computed(() => this.#formValues().quantity ?? 0);
  readonly totalPrice = computed(() => (this.#formValues().quantity ?? 1) * (this.#formValues().unitPrice ?? 0));

  readonly #locationId = computed(() => this.#formValues().locationId);

  readonly roomQuantityResource = rxResourceFixed({
    params: () => {
      const locationId = this.#locationId();
      const addendumId = this.addendumId();
      const product = this.product();
      if (!locationId || !addendumId || !product.productId || !product.uofmId) return undefined;
      return { locationId, addendumId, productId: product.productId, uofmId: product.uofmId };
    },
    defaultValue: null as ProjectModelRoomQuantityLookup | null,
    stream: ({ params }) =>
      this.#modelElevationProductsService.getRoomQuantityLookup(
        params.addendumId,
        params.productId,
        params.locationId,
        params.uofmId
      )
  });

  readonly productAttributesResource = rxResourceFixed({
    params: () => ({ productId: this.product().productId }),
    defaultValue: [] as DsmProductAttributeGroup[],
    stream: ({ params }) => {
      if (!params.productId) return of<DsmProductAttributeGroup[]>([]);
      return this.#modelElevationProductsService.getProductAttributes(params.productId);
    }
  });

  readonly locationOptions = rxResourceFixed({
    params: () => ({
      productId: this.product().productId,
      addendumId: this.addendumId(),
      lotId: this.lotId(),
      preloaded: this.preloadedLocations()
    }),
    defaultValue: [] as DropdownData[],
    stream: ({ params }) => {
      if (params.preloaded === null) return NEVER;
      if (params.preloaded)
        return of(
          params.preloaded.map(location => ({
            id: location.id,
            firstLabel: location.locationCode ?? '—',
            secondLabel: location.locationName
          }))
        );
      if (!params.productId) return of<DropdownData[]>([]);
      return this.#modelElevationProductsService.getScopedProductLocations(params, params.productId).pipe(
        map(locations =>
          locations.map(location => ({
            id: location.id,
            firstLabel: location.locationCode ?? '—',
            secondLabel: location.locationName
          }))
        )
      );
    }
  });

  readonly noMeasurementDefined = computed(() => this.roomQuantityResource.value()?.matchedShape === 'None');

  readonly productAttributes = computed(() => this.productAttributesResource.value() ?? []);
  readonly hasAttributeConfigError = computed(
    () =>
      this.product().selectionRequired &&
      !this.productAttributesResource.isLoading() &&
      this.productAttributes().length === 0
  );
  readonly attributeValueOptions = computed(() => {
    const map = new Map<string, DropdownData[]>();
    this.productAttributes().forEach(attribute => {
      map.set(
        attribute.subCategoryAttributeId,
        attribute.productAttributeValues.map(value => ({
          id: value.id,
          firstLabel: value.value
        }))
      );
    });
    return map;
  });

  get attributeValuesGroup(): FormRecord<FormControl<string | null>> {
    return this.form.controls.attributeValues;
  }

  constructor() {
    effect(() => {
      const initial = this.initialValues();
      const product = this.product();
      this.form.patchValue(
        {
          locationId: initial?.locationId ?? null,
          quantity: initial?.quantity ?? product.quantity,
          unitPrice: initial?.unitPrice ?? product.unitPrice,
          notes: initial?.notes ?? ''
        },
        { emitEvent: true }
      );
    });

    effect(() => {
      const result = this.roomQuantityResource.value();
      if (result) {
        this.form.controls.quantity.setValue(result.quantity, { emitEvent: true });
      }
    });

    effect(() => {
      const attributes = this.productAttributes();
      const formRecord = this.attributeValuesGroup;
      const controls = formRecord.controls;
      const initial = this.initialValues();
      const requiredByProduct = this.product().selectionRequired;

      if (!attributes.length) return;

      const attributeIds = new Set(attributes.map(attribute => attribute.subCategoryAttributeId));

      attributes.forEach(attribute => {
        if (!controls[attribute.subCategoryAttributeId]) {
          const control = new FormControl<string | null>(
            initial?.attributeValues?.[attribute.subCategoryAttributeId] ?? null,
            {
              validators: requiredByProduct ? [Validators.required] : []
            }
          );
          if (this.submitAttempted()) {
            control.markAsTouched();
          }
          formRecord.addControl(attribute.subCategoryAttributeId, control);
        }
      });

      Object.keys(controls).forEach(controlName => {
        if (!attributeIds.has(controlName)) {
          formRecord.removeControl(controlName);
        }
      });
    });

    effect(() => {
      if (this.hasAttributeConfigError()) {
        this.form.setErrors({ ...(this.form.errors ?? {}), attributeConfigError: true });
      } else if (this.form.errors?.['attributeConfigError']) {
        const { attributeConfigError, ...rest } = this.form.errors;
        this.form.setErrors(Object.keys(rest).length ? rest : null);
      }
    });
  }

  attemptCommit(): void {
    this.submitAttempted.set(true);
    this.form.markAllAsTouched();
    if (this.form.invalid || this.hasAttributeConfigError()) return;

    const raw = this.form.getRawValue();
    const attributeValues: Record<string, string> = {};
    Object.entries(this.form.controls.attributeValues.controls).forEach(([id, control]) => {
      const value = control.value;
      if (value) attributeValues[id] = value;
    });

    this.commit.emit({
      locationId: raw.locationId,
      quantity: raw.quantity ?? 0,
      unitPrice: raw.unitPrice ?? 0,
      notes: raw.notes ?? '',
      attributeValues
    });
  }
}
